import { isExtraFastIncluded, isPricingFactorIncluded } from '../packagesUtils';
import { findCalculator } from '../calculator';
import { SCRIPT_PROOFREADING_FEATURE } from '../packagesUtils/constants';

/**
 * Calculates the total price, duration and effected pricing factors for voice over gigs.
 *
 * The effected pricing factors can be either proofreading (price may change), and extraFast
 * (price and duration may change). The calculation takes into account all selected pricing factors,
 * the basic package offer, the additional factors and the script length.
 *
 * @param scriptLength
 * @param calculatorId
 * @param packageData
 * @returns {{duration: number, purchaseExtras: {}, price: number}}
 */
const calculateVoData = ({ scriptLength, calculatorId, packageData }) => {
    const { calculators, features, extraFast, price } = packageData;
    const voData = { duration: 0, price, purchaseExtras: {}, calculatorId };
    const proofreading = extractProofreading(features);

    const { pricingFactorsTotalPrice, pricingFactorsTotalDuration } = sumPricingFactorsTotal(packageData);
    const calculator = findCalculator(calculators, calculatorId);
    voData.price += calculateUnitPrice({ scriptLength, calculator }) + pricingFactorsTotalPrice;

    if (proofreading) {
        const currentPrice = calculateProofreadingPrice({ scriptLength, proofreading });
        voData.purchaseExtras[proofreading.id] = { ...proofreading, ...{ currentPrice } };

        if (isPricingFactorIncluded(proofreading)) {
            voData.price += currentPrice;
            scriptLength += calculateProofreadingEffort({ scriptLength, calculator, proofreading });
        }
    }

    if (extraFast) {
        const { currentPrice, currentDuration } = calculateExtraFastPriceAndDuration({ scriptLength, extraFast });
        voData.purchaseExtras[extraFast.id] = { ...extraFast, ...{ currentPrice, currentDuration } };
        voData.price += isExtraFastIncluded(extraFast) ? currentPrice : 0;
    }

    voData.duration += calculateUnitDuration({ scriptLength, packageData }) + pricingFactorsTotalDuration;

    return voData;
};

/**
 *  The proofreading effort calculates the total script length in case proofreading feature
 *  is included (which may increase the total script length updating the total price and duration)
 */
const calculateProofreadingEffort = ({ scriptLength, calculator, proofreading }) => {
    const proofreadingEffortCoefficient = calculateProofreadingEffortCoefficient({ calculator, proofreading });

    return Math.floor(proofreadingEffortCoefficient * scriptLength);
};

const calculateProofreadingEffortCoefficient = ({ calculator, proofreading }) => {
    const { price, additionalValue } = calculator;

    return proofreadingCoefficientCalculable({ proofreading, additionalValue, price }) ?
        ((additionalValue / proofreading.value) * (proofreading.price / price)) : 0;
};

const proofreadingCoefficientCalculable = ({ proofreading, additionalValue, price }) =>
    proofreadingValuable(proofreading) && additionalValue && price > 0;

const extractProofreading = (features) => features.find((feature) => feature.name === SCRIPT_PROOFREADING_FEATURE);

const calculateProofreadingPrice = ({ scriptLength, proofreading }) => {
    if (proofreadingValuable(proofreading)) {
        const proofreadingQuantity = Math.ceil(scriptLength / proofreading.value);
        return proofreadingQuantity * proofreading.price;
    }

    return proofreading.price;
};

const proofreadingValuable = (proofreading) => proofreading.value > 0 && proofreading.price;

const calculateExtraFastPriceAndDuration = ({ scriptLength, extraFast }) => {
    const currentDuration = Math.ceil(scriptLength / extraFast.value);
    const currentPrice = extraFast.price * currentDuration;
    return { currentPrice, currentDuration };
};

/**
 *  Sums the total price and duration of all non-special pricing factors
 *  (i.e all features except proofreading (which is handled specifically)
 *  and all custom extras. Extra Fast factor is also handled specifically.
 *  The total price and duration is simply calculated by {price | duration} * amount
 */
const sumPricingFactorsTotal = (packageData) => {
    const { features, customExtras } = packageData;
    const total = { pricingFactorsTotalPrice: 0, pricingFactorsTotalDuration: 0 };

    const includedFeatures = features.filter((feature) => isPricingFactorIncluded(feature) && !isProofreading(feature));
    const includedCustomExtras = customExtras.filter(isPricingFactorIncluded);

    includedFeatures.forEach((includedFeature) => {
        total.pricingFactorsTotalPrice += includedFeature.amount * includedFeature.price;
        total.pricingFactorsTotalDuration += includedFeature.currentDuration;
    });

    includedCustomExtras.forEach((includedCustomExtra) => {
        total.pricingFactorsTotalPrice += includedCustomExtra.price;
        total.pricingFactorsTotalDuration += includedCustomExtra.duration;
    });

    return total;
};

const calculateUnitPrice = ({ scriptLength, calculator }) => {
    const { extra, price, additionalValue } = calculator;
    const additionalUnits = scriptLength - extra;

    return additionalUnits > 0 ? Math.ceil(additionalUnits / additionalValue) * price : 0;
};

const calculateUnitDuration = ({ scriptLength, packageData }) => {
    const { extraFast } = packageData;

    return isExtraFastIncluded(extraFast) ?
        calculateExtraFastDuration({ scriptLength, extraFast }) :
        calculateRegularDuration({ scriptLength, packageData });
};


/**
 * Calculates the regular duration (when no extra fast is accounted for)
 *
 * Example:
 * maxNumWordsPerPackageDuration: 400
 * maxNumWordsPerOneMoreDay: 1000
 * duration : 2
 *
 * scriptLength in range of 0 - 400 : 2
 * scriptLength in range of 401 - 1400 : 2 + 1
 * scriptLength in range of 1401 - 2400 : 2 + 1 + 1
 */
const calculateRegularDuration = ({ scriptLength, packageData }) => {
    const { duration, maxNumWordsPerOneMoreDay, maxNumWordsPerPackageDuration } = packageData;

    if (scriptLength <= maxNumWordsPerPackageDuration) {
        return duration;
    }

    const additionalWordEffort = scriptLength - maxNumWordsPerPackageDuration;
    const additionalDays = Math.ceil(additionalWordEffort / maxNumWordsPerOneMoreDay);
    return duration + additionalDays;
};

/**
 * Calculates the extra fast duration (when extra fast is included)
 * Each additional word step will add 1 day to the overall delivery time
 * In other words within 1 day the seller can deliver up to [extraFast value] words.
 *
 * Example:
 * extraFast value: 400
 *
 * scriptLength in range of 0 - 400 : 1
 * scriptLength in range of 401 - 800 : 1 + 1
 * scriptLength in range of 801 - 1200 : 1 + 1 + 1
 */
const calculateExtraFastDuration = ({ scriptLength, extraFast }) => Math.ceil(scriptLength / extraFast.value);

const isProofreading = (pricingFactor) => pricingFactor && (pricingFactor.name === SCRIPT_PROOFREADING_FEATURE);

export default calculateVoData;